import React, { useEffect, useState } from "react";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { authFetch, httpClient } from "../../../HttpClient";
import {
  Building2, X, MessageSquare, CheckCircle2, XCircle,
  Loader2, ClipboardCheck, FileText, Paperclip,
} from "lucide-react";

const CreationSct = ({ selectedClient, setSelectedClient, fetchTodos }) => {
  const [loading, setLoading] = useState(true);
  const [isLoading] = useState(false);
  const [commentaireInput, setCommentaireInput] = useState("");
  const [commentaires, setCommentaires] = useState([]);
  const [filteredModif, setFilteredModif] = useState(null);
  const { utilisateur, patchTodoLocal } = useEtatGlobal();

  useEffect(() => {
    if (selectedClient) setCommentaires(selectedClient.commentaires || []);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient?.formLeadId || !selectedClient?.nomClient) return;
    setLoading(true);
    httpClient
      .get(`https://backend-myalfa.vercel.app/api/formleads/${selectedClient.formLeadId}`)
      .then((res) => {
        const filtered = res.data.data?.ToDo?.find((item) => item.id === selectedClient.id);
        setFilteredModif(filtered || null);
      })
      .catch((err) => console.error("Erreur lors de la récupération du client :", err))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const handleValidation = async () => {
    // UI instantanée : la tâche passe en "Terminer" et la popup se ferme
    // tout de suite, l'appel API se fait en arrière-plan
    const todoId = selectedClient.id;
    patchTodoLocal(todoId, { comptaDext: true, status: "Terminer" });
    setSelectedClient(null);

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/todos/${todoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comptaDext: true, status: "Terminer" }),
      });
      if (!res.ok) throw new Error("Erreur lors de la mise à jour");
      fetchTodos();
    } catch (err) {
      console.error("Erreur lors de la validation :", err);
      fetchTodos(); // resynchronise l'état réel
    }
  };

  const handleAnnulation = async () => {
    // UI instantanée : même principe que la validation
    const todoId = selectedClient.id;
    patchTodoLocal(todoId, { status: "Annuler" });
    setSelectedClient(null);

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/todos/${todoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Annuler" }),
      });
      if (!res.ok) throw new Error("Erreur lors de l'annulation");
      fetchTodos();
    } catch (err) {
      console.error("Erreur lors de l'annulation :", err);
      fetchTodos(); // resynchronise l'état réel
    }
  };

  const handleAddComment = () => {
    const texte = commentaireInput.trim();
    if (!texte) return;
    const nomComplet = `${utilisateur.lastName} ${utilisateur.name}`;
    const optimistic = { nomComplet, commentaire: texte, createdAt: new Date().toISOString(), _pending: true };
    setCommentaires((prev) => [...prev, optimistic]);
    setCommentaireInput("");

    authFetch("https://backend-myalfa.vercel.app/api/commentairesTodo", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nomComplet, commentaire: texte, todoId: selectedClient.id }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setCommentaires((prev) =>
          prev.map((c) => c._pending && c.commentaire === texte ? { ...c, _pending: false } : c)
        );
        fetchTodos();
      })
      .catch(() => {
        setCommentaires((prev) => prev.filter((c) => !(c._pending && c.commentaire === texte)));
      });
  };

  const documents = selectedClient
    ? [selectedClient.bilanDocument, selectedClient.identityDocument, selectedClient.kbisDocument, selectedClient.statusDocument, selectedClient.pieceIdentite].filter(Boolean)
    : [];

  return (
    <div className="overlayAC">
      <div className="inoverlayLead">
        <div className="bg-white rounded-xl w-[90%] mx-auto">

          {/* En-tête */}
          <div className="bg-white border-b-4 border-[#8B1538] px-6 pb-5 pt-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6969eee7337849d9e1d7fcb4/2957c578c_LogoAlfa-PNG-SansCartouche.png"
                  alt="Alfa" className="h-14 object-contain"
                />
                <div className="h-10 w-px bg-slate-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Mission — Immatriculation
                    </span>
                  </div>
                  <h1 className="text-xl font-bold text-slate-900 m-0">{selectedClient?.nomClient}</h1>
                </div>
              </div>
              <button onClick={() => setSelectedClient(null)} className="p-2 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-[#840040]">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : !filteredModif ? (
              <p className="text-sm text-slate-500 py-6 text-center">Aucun détail d'immatriculation trouvé pour cette tâche.</p>
            ) : (
              <>
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <ClipboardCheck className="w-4 h-4 text-[#840040]" />
                    <p className="text-sm font-medium text-slate-700">Validation de la tâche</p>
                  </div>
                  {selectedClient?.title && (
                    <h2 className="text-base font-semibold text-slate-900 mb-3">{selectedClient.title}</h2>
                  )}
                  {selectedClient?.commentaire && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <p className="text-xs uppercase tracking-wide text-slate-400">Description</p>
                      </div>
                      <p className="text-sm text-slate-700">{selectedClient.commentaire}</p>
                    </div>
                  )}

                  {documents.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                        <p className="text-xs uppercase tracking-wide text-slate-400">Documents</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {documents.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#840040] underline hover:text-[#6c0034] transition">
                            Document {i + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedClient.status !== "Terminer" && (
                    <button onClick={handleValidation} disabled={isLoading} className="w-full mb-3 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded-lg transition shadow-sm">
                      {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Validation en cours…</> : <><CheckCircle2 className="w-4 h-4" /> Valider la tâche</>}
                    </button>
                  )}
                  {(utilisateur.role === "ADMINISTRATEUR" || utilisateur.role === "EXPERT_COMPTABLE") && (
                    <button onClick={handleAnnulation} disabled={isLoading} className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded-lg transition shadow-sm">
                      {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Annulation en cours…</> : <><XCircle className="w-4 h-4" /> Annuler la tâche</>}
                    </button>
                  )}
                </div>

                {/* Commentaires */}
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#840040]" />
                    Commentaires
                    {commentaires.length > 0 && (
                      <span className="ml-1 text-[10px] font-medium px-2 py-0.5 rounded-full text-white bg-[#840040]">{commentaires.length}</span>
                    )}
                  </h3>
                  <div className="space-y-3 max-h-40 overflow-y-auto pr-1 mb-4">
                    {commentaires.length > 0 ? commentaires.map((c, i) => (
                      <div key={i} className={`bg-white rounded-lg p-3 border border-[#84004030] transition-opacity ${c._pending ? "opacity-60" : ""}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 bg-[#840040]">{(c.nomComplet || "A")[0]}</div>
                          <div className="flex-1">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-semibold text-slate-800 text-sm">{c.nomComplet || "Anonyme"}</span>
                              {c.createdAt && <span className="text-[11px] text-[#840040]">{new Date(c.createdAt).toLocaleString("fr-FR")}</span>}
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed">{c.commentaire}</p>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-6 text-[#840040]">
                        <MessageSquare className="w-7 h-7 mx-auto mb-2 opacity-40" />
                        <p className="text-xs text-slate-400">Aucun commentaire pour le moment</p>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-[#84004030] pt-4">
                    <textarea value={commentaireInput} onChange={(e) => setCommentaireInput(e.target.value)} placeholder="Ajouter un commentaire..." rows={2} className="w-full text-sm rounded-lg p-3 border border-[#84004040] focus:outline-none focus:ring-2 focus:ring-[#84004040] resize-none" />
                    <div className="flex justify-end mt-2">
                      <button onClick={handleAddComment} disabled={!commentaireInput.trim()} className="text-white text-sm font-medium px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] transition disabled:opacity-50">
                        Envoyer
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pb-2" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreationSct;
